import React, { useEffect, useMemo, useRef, useState } from 'react';
import './HomePage.css';
import TopBar from '../components/TopBar';
import { RoleChip } from '../components/auction/auctionCommon';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import {
  ensureFantasyDataSynced,
  getPlayersFromFirestore,
  getSyncMetadata,
} from '../firebase/playerService';
import { getTotalAuctionsCount, syncUserAuctionCount } from '../firebase/auctionService';
import { getLeaderboard, isSamePerson, QUIZ_MODES } from '../firebase/quizService';
import { getTotalUsersCount } from '../firebase/usersService';
import { getOpenPublicTradesCount } from '../firebase/publicTradesService';
import useFantasyDataSyncing from '../hooks/useFantasyDataSyncing';
import {
  Database,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  RefreshCcw,
  Clock3,
  Users,
  Activity,
  ArrowRight,
  Sparkles,
  Info,
  BarChart3,
  Target,
  Gavel,
  CircleDashed,
  Zap,
  ScrollText,
  HatGlasses,
  BicepsFlexed,
  X,
  Trophy,
  Award,
  UserCheck,
  ArrowLeftRight,
  NotebookPen,
  FlaskConical,
  Gamepad2,
  Radar,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function formatDateTime(ts) {
  if (!ts) return 'Mai';
  const date = new Date(ts);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getRelativeLabel(ts) {
  if (!ts) return 'Mai sincronizzato';
  const diff = Date.now() - Number(ts);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  return `${days} giorni fa`;
}

// Anima un numero da 0 (o dal valore precedente) fino al target quando
// cambia, per dare più "peso" percepito ai contatori della dashboard.
function useCountUp(target, duration = 900) {
  const [display, setDisplay] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    const from = prevTarget.current;
    const to = Number(target) || 0;

    if (from === to) {
      setDisplay(to);
      return undefined;
    }

    let raf;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevTarget.current = to;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
}

function SyncBadge({ status }) {
  if (status === 'loading') {
    return (
      <div className="hp-sync-badge hp-sync-badge--loading" role="status" aria-live="polite">
        <Loader2 size={13} className="spin" />
        <span>Sincronizzazione...</span>
      </div>
    );
  }
  if (status === 'ready') {
    return (
      <div className="hp-sync-badge hp-sync-badge--ready" role="status" aria-live="polite">
        <CheckCircle2 size={13} />
        <span>Dati pronti</span>
      </div>
    );
  }
  return (
    <div className="hp-sync-badge hp-sync-badge--error" role="status" aria-live="polite">
      <AlertTriangle size={13} />
      <span>Errore sync</span>
    </div>
  );
}

function NavCard({ icon: Icon, title, description, onClick, color, soon, locked, lockedLabel }) {
  const disabled = soon || locked;
  return (
    <button
      type="button"
      className={`hp-nav hp-nav--${color} ${disabled ? 'hp-nav--soon' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={
        soon
          ? `${title} — disponibile prossimamente`
          : locked
            ? `${title} — ${lockedLabel}`
            : title
      }
    >
      <div className="hp-nav__glow" aria-hidden="true" />
      <div className="hp-nav__icon" aria-hidden="true">
        <Icon size={24} />
      </div>
      <div className="hp-nav__body">
        <div className="hp-nav__title">
          <strong>{title}</strong>
          {soon && <span className="hp-nav__soon-pill">Presto</span>}
          {locked && <span className="hp-nav__soon-pill">{lockedLabel}</span>}
        </div>
        <span className="hp-nav__desc">{description}</span>
      </div>
      {!disabled && (
        <span className="hp-nav__fab" aria-hidden="true">
          <ArrowRight size={15} />
        </span>
      )}
    </button>
  );
}

const STEPS = [
  {
    icon: RefreshCcw,
    title: 'Sincronizza il database',
    desc: 'Parti con dati freschi: voti e statistiche allineate alla stagione in corso.',
    color: 'cyan',
    num: '01',
    details: [
      'Apri la Home: la sincronizzazione parte in automatico al caricamento.',
      'Trascina verso il basso (pull-to-refresh) per forzare un aggiornamento immediato.',
      'La data dell\'ultimo aggiornamento è sempre visibile nella card in alto a destra.',
    ],
  },
  {
    icon: NotebookPen,
    title: 'Prepara la strategia',
    desc: 'Crea preset di etichette, fasce e note dal tuo account, pronti da importare in ogni asta.',
    color: 'lime',
    num: '02',
    route: '/strategie',
    routeLabel: 'Vai alle Strategie',
    details: [
      'Dal menu Strategie dalla Home crea una nuova strategia.',
      'Imposta etichette, fasce di livello, note e tetto massimo sui giocatori che ritieni più opportuni.',
      'Importala in una o più aste dentro all\'asta stessa.',
      'Modificando i parametri nel tab. Svincolati rimarranno solamente sull1\'asta corrente.',
    ],
  },
  {
    icon: Target,
    title: 'Prepara l\'asta',
    desc: 'Configura budget, slot e regole prima che inizi la sessione d\'asta.',
    color: 'blue',
    num: '03',
    route: '/aste',
    routeLabel: 'Vai alle Aste',
    details: [
      'Crea una nuova asta scegliendo modalità Classic o Mantra.',
      'Imposta budget, squadre partecipanti e slot per ruolo.',
      'Definisci se rendere l\'asta privata o pubblica.',
      'Invita i partecipanti condividendo il nome dell\'asta.',
    ],
  },
  {
    icon: Gamepad2,
    title: 'Divertiti con FantaQuizz',
    desc: 'Mettiti alla prova con i quiz sul fantacalcio e scala la classifica dei manager.',
    color: 'orange',
    num: '04',
    route: '/quiz',
    routeLabel: 'Vai a FantaQuizz',
    details: [
      'Scegli una modalità: Sopravvivenza, Time Attack o Indovina il giocatore.',
      'Rispondi in fretta e senza errori per totalizzare più punti.',
      'Confronta il tuo punteggio con gli altri nella classifica FantaQuizz.',
    ],
  },
  {
    icon: ScrollText,
    title: 'Inserisci i commenti',
    desc: 'Inserisci i commenti sui giocatori da attenzionare.',
    color: 'amber',
    num: '05',
    details: [
      'Nella tab Svincolati individua i giocatori di interesse.',
      'Tocca l\'icona nota per scrivere un commento personale.',
      'Il commento resta visibile in tutte le prossime sessioni.',
    ],
  },
  {
    icon: BicepsFlexed,
    title: 'Domina l\'asta',
    desc: 'Utilizza tutti gli strumenti a disposizione per creare la squadra più forte.',
    color: 'red',
    num: '06',
    details: [
      'Segui in tempo reale acquisti, crediti residui e slot liberi di ogni squadra.',
      'Confronta i giocatori disponibili con statistiche stagione corrente e precedente.',
      'Registra ogni acquisto direttamente dalla tab Market.',
    ],
  },
  {
    icon: Sparkles,
    title: 'Sfrutta l\'AI',
    desc: 'Lasciati suggerire i crediti da spendere e i giocatori per completare la rosa dall\'AI.',
    color: 'violet',
    num: '07',
    details: [
      'Nella tab Formazione consulta i suggerimenti automatici.',
      'Ricevi indicazioni su budget residuo e ruoli scoperti da coprire.',
      'Le analisi si aggiornano man mano che fai nuovi acquisti.',
    ],
  },
  {
    icon: HatGlasses,
    title: 'Osserva gli amici',
    desc: 'Osserva le aste dei tuoi amici.',
    color: 'pink',
    num: '08',
    route: '/aste',
    routeLabel: 'Cerca aste pubbliche',
    details: [
      'Dalla pagina Aste tocca "Aste pubbliche" per cercarne una da seguire.',
      'Aggiungila alla tua lista per consultarla in sola lettura.',
      'Monitora acquisti e formazioni degli avversari in tempo reale.',
    ],
  },
  {
    icon: CircleDashed,
    title: 'Formazione live',
    desc: 'Consulta la migliore formazione man mano che la rosa prende forma.',
    color: 'green',
    num: '09',
    details: [
      'Apri la tab Formazione della tua asta.',
      'Scegli il modulo: il campo si riempie con la tua migliore formazione disponibile.',
      'Tocca un titolare in campo per sostituirlo con una riserva compatibile.',
    ],
  },
  {
    icon: ArrowLeftRight,
    title: 'Scambi',
    desc: 'Trova scambi vantaggiosi per entrambi o proponine uno libero da far votare alla community.',
    color: 'teal',
    num: '10',
    route: '/scambi',
    routeLabel: 'Vai agli Scambi',
    details: [
      'Scambi Win-To-Win: scegli un\'asta, i tuoi giocatori e la squadra con cui scambiare.',
      'Scambi Liberi: proponi uno scambio tra due giocatori qualsiasi, senza bisogno di un\'asta.',
      'Rendi pubblico uno scambio (di entrambi i tipi) per farlo votare dalla community: dopo 4 giorni saprai se è stato approvato.',
    ],
  },
];

function StepItem({ step, index, onOpen }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const Icon = step.icon;

  return (
    <button
      type="button"
      ref={ref}
      className={`hp-step hp-step--${step.color} ${visible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${index * 90}ms` }}
      onClick={() => onOpen(step)}
    >
      <div className="hp-step__num" aria-hidden="true">{step.num}</div>
      <div className={`hp-step__icon-wrap hp-step__icon-wrap--${step.color}`}>
        <Icon size={18} />
      </div>
      <div className="hp-step__body">
        <strong>{step.title}</strong>
        <p>{step.desc}</p>
      </div>
    </button>
  );
}

function StepGuideOverlay({ step, onClose, onNavigate }) {
  if (!step) return null;
  const Icon = step.icon;

  return (
    <div className="hp-guide-backdrop" onClick={onClose}>
      <div
        className={`hp-guide-modal hp-guide-modal--${step.color}`}
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="hp-guide-close" onClick={onClose} aria-label="Chiudi">
          <X size={16} />
        </button>

        <div className="hp-guide-head">
          <div className={`hp-guide-icon hp-guide-icon--${step.color}`}>
            <Icon size={26} />
          </div>
          <div>
            <span className="hp-guide-eyebrow">Passo {step.num}</span>
            <h3>{step.title}</h3>
          </div>
        </div>

        <p className="hp-guide-desc">{step.desc}</p>

        <ol className="hp-guide-list">
          {(step.details || []).map((line, i) => (
            <li key={i} style={{ animationDelay: `${i * 110}ms` }}>
              <span className="hp-guide-list__dot" aria-hidden="true">{i + 1}</span>
              <span>{line}</span>
            </li>
          ))}
        </ol>

        {step.route && (
          <button type="button" className="hp-guide-cta" onClick={() => onNavigate(step.route)}>
            {step.routeLabel || 'Vai'}
            <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { syncing } = useFantasyDataSyncing();
  const [status, setStatus] = useState('loading');
  const [syncMeta, setSyncMeta] = useState(null);
  const [players, setPlayers] = useState([]);
  const [quizLeaderboard, setQuizLeaderboard] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalAuctionsCount, setTotalAuctionsCount] = useState(0);
  const [openTradesCount, setOpenTradesCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const touchStartY = useRef(null);
  const [activeStep, setActiveStep] = useState(null);

  const playersCount = syncMeta?.playersCount || 0;
  const lastUpdatedLabel = useMemo(() => getRelativeLabel(syncMeta?.lastSyncedAt), [syncMeta]);
  const lastUpdatedExact = useMemo(() => formatDateTime(syncMeta?.lastSyncedAt), [syncMeta]);

  const loadDashboard = async ({ force = false } = {}) => {
    try {
      setRefreshing(true);
      setStatus('loading');
      // Mostra subito l'ultimo aggiornamento noto (come la TopBar) invece di
      // "Mai sincronizzato" mentre l'eventuale sync verso l'API è in corso
      const cachedMeta = await getSyncMetadata().catch(() => null);
      if (cachedMeta) setSyncMeta(cachedMeta);

      const result = await ensureFantasyDataSynced({ force });
      const meta = result?.meta || (await getSyncMetadata());
      setSyncMeta(meta || cachedMeta || null);
      setStatus(meta || cachedMeta ? 'ready' : 'error');

      const list = await getPlayersFromFirestore().catch(() => []);
      setPlayers(list);
    } catch (error) {
      console.error(error);
      setStatus('error');
      // Anche se la sync fallisce (es. API esterna irraggiungibile), mostra
      // comunque l'ultimo aggiornamento noto invece di "Mai sincronizzato"
      const fallbackMeta = await getSyncMetadata().catch(() => null);
      if (fallbackMeta) setSyncMeta(fallbackMeta);
    } finally {
      setRefreshing(false);
      setPullDistance(0);
      setIsPulling(false);
      touchStartY.current = null;
    }
  };

  useEffect(() => {
    loadDashboard().catch(() => setStatus('error'));
  }, []);

  // Classifica FantaQuizz: indipendente dalla sync dei dati fantacalcio,
  // quindi caricata a parte al primo render. Il quiz ha ora 3 modalità con
  // classifiche separate: qui le uniamo solo per il widget dashboard
  // (conteggio giocatori distinti + punteggio più alto in assoluto tra le
  // modalità), non per un vero "confronto" tra punteggi non comparabili.
  useEffect(() => {
    Promise.all(QUIZ_MODES.map((mode) => getLeaderboard(200, mode)))
      .then((lists) => {
        const merged = [];
        lists.flat().forEach((entry) => {
          const i = merged.findIndex((x) => isSamePerson(x, entry));
          if (i === -1) merged.push(entry);
          else if ((Number(entry.score) || 0) > (Number(merged[i].score) || 0)) merged[i] = entry;
        });
        merged.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
        setQuizLeaderboard(merged);
      })
      .catch(() => setQuizLeaderboard([]));
  }, []);

  // Utenti registrati: contatore condiviso in appMeta/userStats, aggiornato
  // da recordUserLogin() al primo accesso di ciascun utente (vedi AuthContext).
  useEffect(() => {
    getTotalUsersCount()
      .then(setTotalUsers)
      .catch(() => setTotalUsers(0));
  }, []);

  // Aste totali di TUTTI gli utenti (non solo le proprie): le aste private
  // non sono leggibili dagli altri client, quindi il totale reale non è
  // calcolabile con una query diretta. Ogni utente riporta il proprio
  // numero di aste in appMeta/auctionCounts (vedi syncUserAuctionCount) ad
  // ogni apertura della Home, e il totale è la somma di quei contatori.
  useEffect(() => {
    if (!user?.uid) {
      setTotalAuctionsCount(0);
      return;
    }
    syncUserAuctionCount(user.uid)
      .catch(() => {})
      .finally(() => {
        getTotalAuctionsCount()
          .then(setTotalAuctionsCount)
          .catch(() => setTotalAuctionsCount(0));
      });
  }, [user]);

  // Scambi pubblici aperti (Scambi Liberi): indipendente dalla sync
  // fantacalcio, come le altre statistiche cross-utente.
  useEffect(() => {
    getOpenPublicTradesCount()
      .then(setOpenTradesCount)
      .catch(() => setOpenTradesCount(0));
  }, []);

  const handleTouchStart = (e) => {
    if (window.scrollY > 0) return;
    touchStartY.current = e.touches[0].clientY;
    setIsPulling(true);
  };
  const handleTouchMove = (e) => {
    if (touchStartY.current == null || refreshing) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0 && window.scrollY <= 0) {
      setPullDistance(Math.min(diff * 0.55, 96));
    }
  };
  const handleTouchEnd = () => {
    if (pullDistance > 72 && !refreshing) {
      loadDashboard({ force: true });
      return;
    }
    setPullDistance(0);
    setIsPulling(false);
    touchStartY.current = null;
  };

  // Somma di tutte le statistiche di rendimento tracciate per ogni giocatore
  // (gol fatti/subiti, assist, cartellini, autogol, rigori...), su tutto il
  // roster: rappresenta il volume totale di dati statistici in "pancia".
  const totalStats = useMemo(() => {
    return players.reduce((sum, p) => (
      sum
      + Number(p.goals || 0)
      + Number(p.gotgoals || 0)
      + Number(p.assists || 0)
      + Number(p.ycards || 0)
      + Number(p.rcards || 0)
      + Number(p.owngoals || 0)
      + Number(p.penalties || 0)
      + Number(p.mpenalties || 0)
      + Number(p.spenalties || 0)
    ), 0);
  }, [players]);

  const quizPlayersCount = quizLeaderboard.length;
  const topQuizScorer = quizLeaderboard[0] || null;
  // Nome + punteggio del primo in classifica come descrizione sotto al valore.
  const quizSub = topQuizScorer
    ? `Best user score: ${topQuizScorer.displayName} · ${topQuizScorer.score} pt`
    : 'Nessun punteggio ancora';

  const playersCountAnimated = useCountUp(playersCount);
  const totalStatsAnimated = useCountUp(totalStats);
  const quizPlayersCountAnimated = useCountUp(quizPlayersCount);
  const totalUsersAnimated = useCountUp(totalUsers);
  const totalAuctionsCountAnimated = useCountUp(totalAuctionsCount);
  const openTradesCountAnimated = useCountUp(openTradesCount);

  // Dati condivisi per la sezione statistiche (icona, valore, colore, testo
  // del tooltip).
  const panelStats = [
    { icon: UserCheck, label: 'Utenti', display: totalUsersAnimated.toLocaleString('it-IT'), color: 'pink', title: 'Utenti che hanno effettuato la login su FantApp!' },
    { icon: Award, label: 'Giocatori FantaQuizz', display: quizPlayersCountAnimated.toLocaleString('it-IT'), color: 'violet', title: quizSub },
    { icon: Gavel, label: 'Aste', display: totalAuctionsCountAnimated.toLocaleString('it-IT'), color: 'teal', title: 'Tutte le aste create in FantApp!' },
    { icon: ArrowLeftRight, label: 'Scambi', display: openTradesCountAnimated.toLocaleString('it-IT'), color: 'blue', title: 'Sondaggi aperti da votare' },
    { icon: Database, label: 'Giocatori', display: playersCountAnimated.toLocaleString('it-IT'), color: 'amber', title: 'Totale giocatori nel dataset' },
    { icon: Activity, label: 'Statistiche', display: totalStatsAnimated.toLocaleString('it-IT'), color: 'green', title: 'Statistiche: Gol, assist, cartellini e altro' },
  ];

  return (
    <main className="container hp-page">
      <TopBar />

      <div
        className={`hp-pull ${isPulling ? 'is-pulling' : ''}`}
        style={{ transform: `translateY(${Math.min(pullDistance, 96)}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull hint */}
        <div className={`hp-pull__hint ${pullDistance > 20 ? 'is-visible' : ''}`}>
          <RefreshCcw size={14} />
          <span>{pullDistance > 72 ? 'Rilascia per aggiornare' : 'Tira per aggiornare'}</span>
        </div>

        {/* ── HERO ── */}
        <section className="hp-hero">
          <div className="hp-hero__content">
            <div className="hp-hero__left">
              <div className="hp-hero__eyebrow">
                <Zap size={13} />
                <span>Control Room</span>
              </div>

              <div className="hp-hero__title-row">
                <div className="hp-hero__logo-wrap">
                  <img src="/logoFantApp.png" alt="FantApp" className="hp-hero__logo" />
                </div>
                <div>
                  <h1 className="hp-hero__h1">FantApp!</h1>
                  <p className="hp-hero__tagline">
                    La cabina di regia per vincere la tua asta di fantacalcio.
                  </p>
                </div>
              </div>

              <p className="hp-hero__desc">
                Il tuo Fantacalcio senza più carta e penna!.
              </p>
            </div>

            {/* Fascia icone decorative: colonna dedicata del grid, in flusso
                normale tra il blocco titolo e la card "Ultimo aggiornamento".
                Le tile sono item di un grid interno (nessun position:absolute
                sciolto sul contenuto) — non possono quindi mai sconfinare
                sopra il testo delle colonne vicine, a nessuna larghezza. */}
            <div className="hp-hero__icons" aria-hidden="true">
              {/* Aste: martelletto disegnato, coerente con lo stile illustrato
                  delle altre tile (appunti, campetto) invece della semplice
                  icona monocolore. */}
              <div className="hp-hero__icon-tile hp-hero__icon-tile--p1">
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  <rect x="8" y="48" width="34" height="10" rx="3" fill="var(--hp-accent)" />
                  <g transform="rotate(-28 26 24)">
                    <rect x="18" y="20" width="9" height="28" rx="4.5" fill="#8a5a34" />
                    <rect x="4" y="8" width="34" height="15" rx="7.5" fill="#d8a06a" stroke="#8a5a34" strokeWidth="2" />
                  </g>
                </svg>
              </div>

              <div className="hp-hero__icon-tile hp-hero__icon-tile--p2">
                <div className="hp-hero__roles-grid">
                  <RoleChip role="P" />
                  <RoleChip role="D" />
                  <RoleChip role="C" />
                  <RoleChip role="A" />
                </div>
              </div>

              <div className="hp-hero__icon-tile hp-hero__icon-tile--p3">
                <div className="hp-hero__icon-pie" />
              </div>

              <div className="hp-hero__icon-tile hp-hero__icon-tile--p4">
                <div className="hp-hero__icon-bars">
                  <span style={{ height: '45%', background: 'var(--hp-blue)' }} />
                  <span style={{ height: '75%', background: 'var(--hp-accent)' }} />
                  <span style={{ height: '58%', background: 'var(--hp-amber)' }} />
                  <span style={{ height: '95%', background: 'var(--hp-teal)' }} />
                </div>
              </div>

              <div className="hp-hero__icon-tile hp-hero__icon-tile--p5">
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  <rect x="10" y="14" width="44" height="42" rx="6" fill="var(--card)" stroke="var(--border)" strokeWidth="1.5" />
                  <circle cx="21" cy="12" r="3.4" fill="var(--hp-accent)" />
                  <circle cx="32" cy="12" r="3.4" fill="var(--hp-blue)" />
                  <circle cx="43" cy="12" r="3.4" fill="var(--hp-amber)" />
                  <line x1="18" y1="28" x2="46" y2="28" stroke="var(--muted)" strokeWidth="2.2" strokeLinecap="round" />
                  <line x1="18" y1="37" x2="46" y2="37" stroke="var(--muted)" strokeWidth="2.2" strokeLinecap="round" />
                  <line x1="18" y1="46" x2="36" y2="46" stroke="var(--muted)" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </div>

              <div className="hp-hero__icon-tile hp-hero__icon-tile--p6">
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  <rect x="4" y="10" width="56" height="44" rx="6" fill="#2e9e4f" />
                  <rect x="9" y="15" width="46" height="34" rx="3" fill="none" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="1.6" />
                  <line x1="32" y1="15" x2="32" y2="49" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="1.6" />
                  <circle cx="32" cy="32" r="7" fill="none" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="1.6" />
                  <circle cx="18" cy="24" r="2.6" fill="var(--hp-amber)" />
                  <circle cx="18" cy="40" r="2.6" fill="var(--hp-amber)" />
                  <circle cx="46" cy="32" r="2.6" fill="#ef4444" />
                </svg>
              </div>

              {/* Fantacalcio */}
              <div className="hp-hero__icon-tile hp-hero__icon-tile--p7">
                <Trophy size={34} color="var(--hp-amber)" strokeWidth={1.8} />
              </div>
            </div>

            <aside className="hp-hero__right">
              <SyncBadge status={status} />

              <div className="hp-hero__sync-card">
                <div className="hp-hero__sync-top">
                  <Clock3 size={14} />
                  <span>Ultimo aggiornamento</span>
                </div>
                <strong className="hp-hero__sync-relative">{lastUpdatedLabel}</strong>
                <span className="hp-hero__sync-exact">{lastUpdatedExact}</span>

                <button
                  className="hp-hero__refresh-btn"
                  onClick={() => loadDashboard({ force: true })}
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <><Loader2 size={14} className="spin" /> Aggiorno...</>
                  ) : (
                    <><RefreshCcw size={14} /> Aggiorna ora</>
                  )}
                </button>
              </div>
            </aside>
          </div>
        </section>

        {/* ── STATISTICHE: riga statica stile "scoreboard" (come l'opzione
            ticker, ma senza lo scorrimento continuo) — icona colorata,
            valore e etichetta per ognuna, va a capo se non c'è spazio. ── */}
        <section className="hp-stat-row" aria-label="Statistiche generali">
          {panelStats.map((item) => (
            <Tooltip as="div" className="hp-stat-row__item" key={item.label} content={item.title}>
              <span className={`hp-stat-row__icon hp-stat-row__icon--${item.color}`}>
                <item.icon size={15} />
              </span>
              <strong>{item.display}</strong>
              <span className="hp-stat-row__label">{item.label}</span>
            </Tooltip>
          ))}
        </section>

        {/* ── NAV CARDS ── */}
        <section className="hp-nav-grid" aria-label="Sezioni principali">
          <NavCard
            icon={Users}
            title="Lista Giocatori"
            description="Il classico listone dei giocatori con filtri e statistiche"
            onClick={() => navigate('/players')}
            color="teal"
          />
          <NavCard
            icon={Gavel}
            title="Aste"
            description="Il centro di FantApp: gestisci e domina le tue aste!"
            onClick={() => navigate('/aste')}
            color="violet"
            locked={syncing}
            lockedLabel="Sync in corso"
          />
          <NavCard
            icon={ArrowLeftRight}
            title="Scambi"
            description="Studia i migliori scambi da proporre e confrontati con la community"
            onClick={() => navigate('/scambi')}
            color="cyan"
          />
          <NavCard
            icon={FlaskConical}
            title="Simula rosa"
            description="Costruisci una rosa e valutala prima di scendere in asta"
            onClick={() => navigate('/simula-rosa')}
            color="green"
          />
          <NavCard
            icon={Radar}
            title="Panoramica squadra"
            description="La formazione consigliata per la prossima giornata, coi dubbi giocatore per giocatore"
            onClick={() => navigate('/panoramica-squadra')}
            color="blue"
          />
          <NavCard
            icon={BarChart3}
            title="FantaQuizz!"
            description="Quiz e sfide dedicate al fantacalcio. Raggiungi la cima della classifica!"
            onClick={() => navigate('/quiz')}
            color="amber"
          />
          <NavCard
            icon={Info}
            title="Informazioni"
            description="Che cos'è FantApp, come nasce e quali obiettivi si pone"
            onClick={() => navigate('/info')}
            color="pink"
          />
        </section>

        {/* ── ROADMAP ── */}
        <section className="hp-roadmap" aria-label="Come usare FantApp">
          <div className="hp-roadmap__head">
            <h2 className="hp-roadmap__title">Come usare FantApp</h2>
            <span className="hp-roadmap__sub">I dieci passi per dominare l'asta.</span>
          </div>

          <div className="hp-roadmap__steps">
            {STEPS.map((step, i) => (
              <StepItem key={step.num} step={step} index={i} onOpen={setActiveStep} />
            ))}
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="hp-footer">
          <span>FantApp — All rights reserved — Sorgenti giocatori</span>
          <a href="https://www.fantamaster.it/" target="_blank" rel="noopener noreferrer">
            FantaMaster
          </a>
        </footer>
      </div>

      <StepGuideOverlay
        step={activeStep}
        onClose={() => setActiveStep(null)}
        onNavigate={(route) => { setActiveStep(null); navigate(route); }}
      />
    </main>
  );
}
